import { forwardRef, useState, type ReactNode } from "react";
import type { ListResponse } from "@shared-core/data/adapter";
import type {
  AcceptedAtPrice,
  AmendmentAnswerInput,
  CancellationAnswerInput,
  DespatchInput,
  LineAction,
  ProposeDateInput,
  ProposedDateAnswer,
  PurchaseOrderLine,
  ServiceRef,
  SupplierFile,
  SupplierKitDrop,
} from "@shared-core/data/supplier-portal-adapter";
import {
  ACTION_WORDING,
  PAGE_WORDING,
  acceptedAtPriceSentence,
  activitiesSummary,
  cancellationRequestedSentence,
  changeToAnswerSentence,
  count,
  deliverToLabel,
  deliveryDoneSentence,
  despatchWording,
  lineActionLabel,
  lineHeading,
  lineQuantity,
  lineStatus,
  postageAccountLabel,
  proofStatus,
  proposedDateAnswerSentence,
  supplierProductLabel,
  versionsSummary,
} from "@shared-core/domain/supplier-portal-wording";
import { Card } from "../../app/components/primitives/card";
import { Button } from "../../app/components/primitives/button";
import { cn } from "../../app/components/primitives/utils";
import { StatusPill } from "../../app/components/domain/StatusPill";
import { StaffViewReadOnly } from "../../app/components/domain/StaffViewReadOnly";
import { KitDrops } from "./KitDrops";
import {
  AcceptAtPriceForm,
  AmendmentRefusalForm,
  CancellationForm,
  DespatchForm,
  ProofUploadForm,
  ProposeDateForm,
  RejectForm,
} from "./LineForms";

/** The form a line has open, one at a time across the order. */
export type LineForm =
  | "accept_at_price"
  | "propose_date"
  | "reject"
  | "mark_despatched"
  | "upload_proof"
  | "confirm_cancellation"
  | "refuse_cancellation"
  | "refuse_amendment";

export interface LineWrites {
  accept: (lineNo: number) => Promise<void>;
  /** Accept the line at a delivered price of the supplier's own, in pence ex VAT (spec 30 §L2e). */
  acceptAtPrice: (
    lineNo: number,
    costPence: number,
  ) => Promise<AcceptedAtPrice>;
  proposeDate: (
    lineNo: number,
    input: ProposeDateInput,
  ) => Promise<ProposedDateAnswer>;
  reject: (lineNo: number, reason: string) => Promise<void>;
  answerCancellation: (
    lineNo: number,
    input: CancellationAnswerInput,
  ) => Promise<void>;
  answerAmendment: (
    lineNo: number,
    input: AmendmentAnswerInput,
  ) => Promise<void>;
  despatch: (lineNo: number, input: DespatchInput) => Promise<void>;
  uploadProof: (lineNo: number, file: File) => Promise<void>;
  /** Fetches and saves the files; rejects when one couldn't be fetched. */
  download: (files: SupplierFile[]) => Promise<void>;
  /** One page of a kit line's drops, searched on the server. */
  kitDrops: (
    dropsUrl: string,
    options: { q?: string; page: number; pageSize: number },
  ) => Promise<ListResponse<SupplierKitDrop>>;
  /** Fetches and saves a kit's manifest; rejects when it couldn't be fetched. */
  downloadManifest: (manifestUrl: string) => Promise<void>;
}

/**
 * One purchase-order line (supplier orders map 1:1 to our lines, so each has its own actions): what to make, in
 * the supplier's own product terms where mapped, the versions, artwork, proof and delivery, what has gone, and the
 * actions the server allows now.
 */
export const LineCard = forwardRef<
  HTMLDivElement,
  {
    line: PurchaseOrderLine;
    lines: readonly PurchaseOrderLine[];
    orderService: ServiceRef | null;
    today: string;
    openForm: LineForm | null;
    onOpenForm: (form: LineForm | null) => void;
    writes: LineWrites;
    isAccepting: boolean;
    error: string | null;
    /** What Pollards answered to the date this supplier just proposed for the line, until the page is left. */
    proposedDateAnswer?: ProposedDateAnswer | null;
    purchaseOrderNo: string;
    /** What the supplier's next price increase this month is called ("2nd"), for the wording before it confirms. */
    nextPriceIncrease?: string | null;
    /** What Pollards answered to the price this supplier just gave for the line, until the page is left. */
    acceptedAtPrice?: AcceptedAtPrice | null;
  }
>(function LineCard(
  {
    line,
    lines,
    orderService,
    today,
    openForm,
    onOpenForm,
    writes,
    isAccepting,
    error,
    proposedDateAnswer = null,
    purchaseOrderNo,
    nextPriceIncrease = null,
    acceptedAtPrice = null,
  },
  ref,
) {
  const status = lineStatus(line);
  const service = line.service ?? orderService;
  const close = () => onOpenForm(null);
  const done = async (write: () => Promise<unknown>) => {
    await write();
    close();
  };

  return (
    <Card
      ref={ref}
      role="group"
      aria-label={`Line ${line.line_no}`}
      className={cn(
        "gap-3 p-4",
        openForm && "border-primary ring-[3px] ring-primary/15",
      )}
    >
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1.5">
        <h3 className="w-full min-w-0 font-semibold sm:w-auto sm:min-w-[14rem] sm:flex-1">
          {lineHeading(line)}
        </h3>
        <span className="text-sm text-muted-foreground tabular-nums">
          {lineQuantity(line)}
        </span>
        <StatusPill label={status.label} tone={status.tone} />
      </div>

      <dl className="grid gap-x-3 gap-y-1 text-sm sm:grid-cols-[10rem_minmax(0,1fr)]">
        {line.quote_reference && (
          <Row term="Your quote">{line.quote_reference}</Row>
        )}
        {line.customer_order_number && (
          <Row term="Your customer's PO number">
            {line.customer_order_number}
            <span className="block text-muted-foreground">
              Put this on the delivery note.
            </span>
          </Row>
        )}
        {line.supplier_product && (
          <Row term="Your product">
            {supplierProductLabel(line.supplier_product)}
          </Row>
        )}
        {line.versions.length > 1 && (
          <Row term="Versions">{versionsSummary(line)}</Row>
        )}
        {line.service && (
          <>
            <Row term="Activities">
              {activitiesSummary(line.service.activities)}
            </Row>
            {line.service.postage && (
              <Row term="Postage">
                {line.service.postage.service} ·{" "}
                <strong>
                  {postageAccountLabel(line.service.postage.account)}
                </strong>
              </Row>
            )}
          </>
        )}
        {line.artwork.length > 0 && (
          <Row term="Artwork">
            <Files files={line.artwork} onDownload={writes.download} />
          </Row>
        )}
        {line.proof && (
          <Row term="Proof">
            <StatusPill {...proofStatus(line.proof)} />
            {line.proof.note && (
              <span className="ml-2 text-muted-foreground">
                {line.proof.note}
              </span>
            )}
          </Row>
        )}
        {/* A mailing is posted, not delivered: its date is the order's "post by". */}
        {line.service?.kind !== "mailing" && (
          <Row term="Deliver to">{deliverToLabel(line, lines)}</Row>
        )}
        {line.rejection_reason && (
          <Row term="Your reason">{line.rejection_reason}</Row>
        )}
      </dl>

      {line.kit && (
        <KitDrops
          kit={line.kit}
          loadDrops={writes.kitDrops}
          onDownloadManifest={writes.downloadManifest}
        />
      )}

      {line.deliveries.map((delivery) => (
        <p
          key={delivery.delivery_no}
          className="text-sm font-semibold text-green-700"
        >
          ✓ {deliveryDoneSentence(delivery, service)}
          {line.deliveries.length > 1 && (
            <span className="font-normal text-muted-foreground">
              {" "}
              ({count(delivery.quantity)})
            </span>
          )}
        </p>
      ))}
      {line.status === "part_despatched" && (
        <p className="text-sm text-muted-foreground">
          {count(line.quantity_to_send)} still to send.
        </p>
      )}

      {error && (
        <p role="alert" className="text-sm text-red-600">
          {error}
        </p>
      )}

      {line.status === "cancel_requested" && (
        <p role="status" className="text-sm font-semibold">
          {cancellationRequestedSentence(line.cancel_answer_by)}
        </p>
      )}

      {line.change_to_answer && (
        <p role="status" className="text-sm font-semibold">
          {changeToAnswerSentence(line.change_to_answer)}
        </p>
      )}

      {proposedDateAnswer && (
        <p role="status" className="text-sm">
          {proposedDateAnswerSentence(proposedDateAnswer)}
        </p>
      )}

      {acceptedAtPrice && (
        <p role="status" className="text-sm">
          {acceptedAtPriceSentence(purchaseOrderNo, acceptedAtPrice)}
        </p>
      )}

      {openForm === "accept_at_price" && (
        <AcceptAtPriceForm
          line={line}
          purchaseOrderNo={purchaseOrderNo}
          nextIncrease={nextPriceIncrease}
          onCancel={close}
          onSubmit={async (costPence) => {
            const answer = await writes.acceptAtPrice(line.line_no, costPence);
            close();
            return answer;
          }}
        />
      )}

      {openForm === "propose_date" && (
        <ProposeDateForm
          line={line}
          onCancel={close}
          onSubmit={(input) =>
            done(() => writes.proposeDate(line.line_no, input))
          }
        />
      )}
      {openForm === "reject" && (
        <RejectForm
          line={line}
          onCancel={close}
          onSubmit={(reason) => done(() => writes.reject(line.line_no, reason))}
        />
      )}
      {(openForm === "confirm_cancellation" ||
        openForm === "refuse_cancellation") && (
        <CancellationForm
          line={line}
          mode={openForm === "confirm_cancellation" ? "confirmed" : "refused"}
          onCancel={close}
          onSubmit={(input) =>
            done(() => writes.answerCancellation(line.line_no, input))
          }
        />
      )}
      {openForm === "refuse_amendment" && (
        <AmendmentRefusalForm
          line={line}
          onCancel={close}
          onSubmit={(reason) =>
            done(() =>
              writes.answerAmendment(line.line_no, {
                outcome: "refused",
                reason,
              }),
            )
          }
        />
      )}
      {openForm === "mark_despatched" && (
        <DespatchForm
          line={line}
          service={service}
          today={today}
          onCancel={close}
          onSubmit={(input) => done(() => writes.despatch(line.line_no, input))}
        />
      )}
      {openForm === "upload_proof" && (
        <ProofUploadForm
          line={line}
          onCancel={close}
          onSubmit={(file) =>
            done(() => writes.uploadProof(line.line_no, file))
          }
        />
      )}

      {openForm === null && line.actions.length > 0 && (
        <StaffViewReadOnly>
          <div className="flex flex-wrap gap-2">
            {line.actions.includes("answer_cancellation") && (
              <>
                <Button
                  disabled={isAccepting}
                  onClick={() => onOpenForm("confirm_cancellation")}
                >
                  {ACTION_WORDING.confirmCancellation}
                </Button>
                <Button
                  variant="outline"
                  disabled={isAccepting}
                  onClick={() => onOpenForm("refuse_cancellation")}
                >
                  {ACTION_WORDING.cannotCancel}
                </Button>
              </>
            )}
            {line.actions.includes("answer_amendment") && (
              <>
                <Button
                  disabled={isAccepting}
                  onClick={() =>
                    void writes.answerAmendment(line.line_no, {
                      outcome: "accepted",
                    })
                  }
                >
                  {ACTION_WORDING.acceptChange}
                </Button>
                <Button
                  variant="outline"
                  disabled={isAccepting}
                  onClick={() => onOpenForm("refuse_amendment")}
                >
                  {ACTION_WORDING.cannotMakeChange}
                </Button>
              </>
            )}
            {line.actions
              .filter(
                (action) =>
                  action !== "answer_cancellation" &&
                  action !== "answer_amendment",
              )
              .map((action) => (
                <ActionButton
                  key={action}
                  action={action}
                  service={service}
                  disabled={isAccepting}
                  onClick={() =>
                    action === "accept"
                      ? void writes.accept(line.line_no)
                      : onOpenForm(action)
                  }
                />
              ))}
          </div>
        </StaffViewReadOnly>
      )}
    </Card>
  );
});

function Row({ term, children }: { term: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-muted-foreground">{term}</dt>
      <dd className="mb-1 min-w-0 sm:mb-0">{children}</dd>
    </>
  );
}

/**
 * One file: its name and Download. Several: how many and Download all. A failed download says so beside the
 * button, so the supplier knows the file didn't arrive.
 */
function Files({
  files,
  onDownload,
}: {
  files: SupplierFile[];
  onDownload: (files: SupplierFile[]) => Promise<void>;
}) {
  const [isBusy, setIsBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const download = async () => {
    setIsBusy(true);
    setFailed(false);
    try {
      await onDownload(files);
    } catch {
      setFailed(true);
    } finally {
      setIsBusy(false);
    }
  };
  const failure = failed && (
    <p role="alert" className="text-sm text-red-600">
      {PAGE_WORDING.downloadFailed(files.length)}
    </p>
  );
  if (files.length === 1) {
    const [only] = files;
    return (
      <>
        <span className="inline-flex flex-wrap items-center gap-x-1">
          <span className="break-all">
            {only.file_name}
            {only.note && (
              <span className="text-muted-foreground"> · {only.note}</span>
            )}
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-muted-foreground"
            aria-label={`${ACTION_WORDING.download} ${only.file_name}`}
            disabled={isBusy}
            onClick={() => void download()}
          >
            {ACTION_WORDING.download}
          </Button>
        </span>
        {failure}
      </>
    );
  }
  return (
    <>
      <span className="inline-flex flex-wrap items-center gap-x-1">
        {files.length} files
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-muted-foreground"
          disabled={isBusy}
          onClick={() => void download()}
        >
          {ACTION_WORDING.downloadAll}
        </Button>
      </span>
      {failure}
    </>
  );
}

const PRIMARY: ReadonlySet<LineAction> = new Set(["accept", "mark_despatched"]);

function ActionButton({
  action,
  service,
  disabled,
  onClick,
}: {
  action: LineAction;
  service: ServiceRef | null;
  disabled: boolean;
  onClick: () => void;
}) {
  const { label, short } = lineActionLabel(action, service);
  return (
    <Button
      variant={PRIMARY.has(action) ? "default" : "outline"}
      className={cn(action === "reject" && "text-red-600 hover:text-red-700")}
      aria-label={short !== label ? label : undefined}
      disabled={disabled}
      onClick={onClick}
    >
      {short !== label ? (
        <>
          <span className="sm:hidden">{short}</span>
          <span className="hidden sm:inline">{label}</span>
        </>
      ) : (
        label
      )}
    </Button>
  );
}

/** The despatch verb for the Needs you box's button ("Mark posted"). */
export function despatchActionLabel(service: ServiceRef | null): string {
  return despatchWording(service).action;
}
